<template>
  <div class="auth-form">
    <div class="auth-form__modal">
      <div class="auth-form__title">Вход или регистрация</div>

      <PhoneForm
        v-if="withPhoneAuth && step === ESteps.phone"
        :error-message="phoneStepErrorMessage"
        :is-visible-captcha="isVisibleCaptcha"
        :captcha-error="captchaError"
        :is-loading="isLoading"
        @send="sendCode"
      />

      <MoAuth v-if="withMotmomAuth && step === ESteps.phone" />

      <CodeForm
        v-if="step === ESteps.code && allowSendAt"
        :is-registration="isRegistration"
        :allow-send-at="allowSendAt"
        :phone="phone"
        :is-loading="isLoading"
        :error-message="codeStepErrorMessage"
        @confirm="validateCode"
        @send-new-code="sendNewCode"
        @change-phone="goToFirstStep"
      />

      <div
        v-if="!withMotmomAuth && !withPhoneAuth"
        class="auth-form__empty-auth-list"
      >
        Нет выбранных типов авторизации
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MoAuth from './MoAuth/MoAuth.vue';

import PhoneForm from './PhoneForm.vue';
import CodeForm from './CodeForm.vue';

const CAPTCHA_ERROR_CODE = '1011';
const NEED_CAPTCHA_ERROR = '1013';

enum ESteps {
  phone = 'phone',
  code = 'code',
}

type ISendCodeF = (
  phone: string,
  catchaToken: string | null,
) => Promise<{ allowSendAt: string; isRegistration: boolean }>;
type IValidateCodeF = (phone: string, code: string) => Promise<unknown>;
type IFetchUserF = () => Promise<unknown>;

const props = defineProps({
  withPhoneAuth: {
    type: Boolean,
    default: false,
  },
  withMotmomAuth: {
    type: Boolean,
    default: false,
  },
  sendCodeRequest: {
    type: Function as PropType<ISendCodeF>,
    default: () => {},
  },
  validateCodeRequest: {
    type: Function as PropType<IValidateCodeF>,
    default: () => {},
  },
  fetchUser: {
    type: Function as PropType<IFetchUserF>,
    default: () => {},
  },
});

const emits = defineEmits(['close']);

const step = ref<ESteps>(ESteps.phone);
const phone = ref<string>('');
const phoneStepErrorMessage = ref<string>('');
const codeStepErrorMessage = ref<string>('');
const allowSendAt = ref<string>('1');
const isRegistration = ref<boolean | undefined>(undefined);
const isLoading = ref<boolean>(false);
const isVisibleCaptcha = ref<boolean>(false);
const captchaError = ref<string>('');

const goToFirstStep = () => {
  step.value = ESteps.phone;
  phone.value = '';
  phoneStepErrorMessage.value = '';
  codeStepErrorMessage.value = '';
  isVisibleCaptcha.value = false;
};

const sendNewCode = () => {
  step.value = ESteps.phone;
  phoneStepErrorMessage.value = '';
  codeStepErrorMessage.value = '';
};

const sendCode = async (currentPhone: string, captchaToken: string | null) => {
  phone.value = currentPhone;
  phoneStepErrorMessage.value = '';

  let response;

  isLoading.value = true;

  try {
    response = await props.sendCodeRequest(phone.value, captchaToken);
  } catch (error: any) {
    // TODO Унифицировать все ошибки
    const { code = '', detail = 'Произошла ошибка' } = error?.response?._data as Record<
      string,
      any
    >;

    if (code === NEED_CAPTCHA_ERROR) {
      isVisibleCaptcha.value = true;
      return;
    }

    if (code === CAPTCHA_ERROR_CODE) {
      captchaError.value = detail;
      return;
    }

    phoneStepErrorMessage.value = detail;

    return;
  } finally {
    isLoading.value = false;
  }

  allowSendAt.value = response.allowSendAt;
  isRegistration.value = response.isRegistration;

  step.value = ESteps.code;
};

const validateCode = async (code: string) => {
  isLoading.value = true;
  codeStepErrorMessage.value = '';

  try {
    await props.validateCodeRequest(phone.value, code);
  } catch (error: any) {
    isLoading.value = false;

    codeStepErrorMessage.value = (error?.response?._data as Record<string, any>).detail;

    return;
  }

  await props.fetchUser();

  isLoading.value = false;

  emits('close');
};
</script>

<style lang="scss" scoped>
.auth-form {
  position: relative;
  z-index: 9999;

  &__empty-auth-list {
    text-align: center;
  }

  &__modal {
    color: #000;
    box-sizing: border-box;
    box-shadow: 0px 0px 50px rgb(0 0 0 / 30%);
    background-color: #fff;
    font-size: 14px;
    font-family: 'Opensans';
    font-weight: 400;
    box-sizing: border-box;
    padding: 60px;
    width: 100%;
  }

  &__title {
    text-transform: uppercase;
    text-align: center;
    font-size: 23px;
    line-height: 25px;
    font-family: 'Gilroy-Medium', sans-serif;
    margin-bottom: 64px;
  }
}

@media (min-width: 481px) {
  .auth-form {
    &__modal {
      width: 480px;
    }
  }
}

@media (max-width: 480px) {
  .auth-form {
    &__modal {
      padding: 40px;
    }
  }
}

@media (max-width: 375px) {
  .auth-form {
    &__modal {
      padding: 40px 24px;
    }
  }
}
</style>
